import React, { useState, useEffect, useCallback } from 'react';
import { Truck, Menu, BarChart3, Users, Shield, AlertTriangle, Settings, LogOut, UserCog, FileText, Upload, CreditCard, Edit3, Archive, UserPlus, Container } from 'lucide-react';
import { Toaster, toast } from '../ui/simple-toaster';
import { api } from '../../utils/api';
import DriversPanel from './DriversPanel';
import VehiclesPanel from './VehiclesPanel';
import CompliancePanel from './CompliancePanel';
import StaffPanel from './StaffPanel';
import ReportsPanel from './ReportsPanel';
import DataImportExportPanel from './DataImportExportPanel';
import SubscriptionPanel from './SubscriptionPanel';
import RecordKeeperPanel from './RecordKeeperPanel';
import ArchiveReportsPanel from './ArchiveReportsPanel';
import FleetInvitesPanel from './FleetInvitesPanel';
import TrailersPanel from './TrailersPanel';

import OverviewTab from './dashboard/OverviewTab';
import ProvisionalTab from './dashboard/ProvisionalTab';
import BusinessRulesTab from './dashboard/BusinessRulesTab';
import NotificationDropdown from './dashboard/NotificationDropdown';
import PushToggle from './dashboard/PushToggle';

const Dashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    if (user?.role === 'RECORD_KEEPER') return 'records';
    return 'overview';
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [provisionalVehicles, setProvisionalVehicles] = useState([]);
  const [pvLoading, setPvLoading] = useState(false);

  const [clientConfig, setClientConfig] = useState({
    work_limit_seconds: 10800,
    rest_requirement_seconds: 1200,
    warning_before_seconds: 600,
    alert_before_start_work_seconds: 300
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [statutoryRounding, setStatutoryRounding] = useState(true);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await api('/notifications?limit=20');
      setNotifications(res.notifications || []);
      setUnreadCount(res.unread_count || 0);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    loadData();
    loadNotifications();
    const notifInterval = setInterval(loadNotifications, 60000);
    const handleResize = () => setSidebarOpen(window.innerWidth >= 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearInterval(notifInterval);
    };
  }, [loadNotifications]);

  const loadData = async () => {
    try {
      console.log('[Dashboard] loadData starting...');
      const results = await Promise.allSettled([
        api('/dashboard/stats'),
        api('/drivers'),
        api('/vehicles'),
        api('/client-settings')
      ]);

      const [statsResult, driversResult, vehiclesResult, settingsResult] = results;

      console.log('[Dashboard] stats:', statsResult.status, statsResult.status === 'fulfilled' ? statsResult.value : statsResult.reason?.message);
      console.log('[Dashboard] drivers:', driversResult.status);
      console.log('[Dashboard] vehicles:', vehiclesResult.status);
      console.log('[Dashboard] settings:', settingsResult.status);

      if (statsResult.status === 'fulfilled') setStats(statsResult.value);
      if (driversResult.status === 'fulfilled') setDrivers(driversResult.value?.drivers || []);
      if (vehiclesResult.status === 'fulfilled') setVehicles(vehiclesResult.value?.vehicles || []);
      if (settingsResult.status === 'fulfilled') {
        setStatutoryRounding(settingsResult.value?.settings?.statutory_rounding ?? true);
      }

      if (['CLIENT_ADMIN', 'SYSTEM_ADMIN'].includes(user.role)) {
        loadProvisionalVehicles();
        loadClientConfig();
      }
    } catch (err) {
      console.error('[Dashboard] Failed to load data:', err);
    }
  };

  const loadProvisionalVehicles = async () => {
    setPvLoading(true);
    try {
      const result = await api('/vehicles/provisional');
      setProvisionalVehicles(result.vehicles || []);
    } catch (err) {
      console.error('Failed to load provisional vehicles:', err);
    } finally {
      setPvLoading(false);
    }
  };

  const loadClientConfig = async () => {
    try {
      const result = await api('/client/diary-config');
      if (result) {
        setClientConfig({
          work_limit_seconds: result.work_limit_seconds || 10800,
          rest_requirement_seconds: result.rest_requirement_seconds || 1200,
          warning_before_seconds: result.warning_before_seconds || 600,
          alert_before_start_work_seconds: result.alert_before_start_work_seconds || 300
        });
      }
    } catch (err) {
      console.error('Failed to load client config:', err);
    }
  };

  const handleApproveVehicle = async (vehicleId) => {
    try {
      await api(`/vehicles/${vehicleId}/approve`, { method: 'POST' });
      toast.success('Vehicle approved');
      loadProvisionalVehicles();
      loadData();
    } catch { toast.error('Failed to approve vehicle'); }
  };

  const handleRejectVehicle = async (vehicleId) => {
    try {
      await api(`/vehicles/${vehicleId}/reject`, { method: 'POST' });
      toast.success('Vehicle rejected');
      loadProvisionalVehicles();
    } catch { toast.error('Failed to reject vehicle'); }
  };

  const handleSaveConfig = async () => {
    setConfigLoading(true);
    try {
      await api('/client/diary-config', {
        method: 'PUT',
        body: JSON.stringify(clientConfig)
      });
      toast.success('Configuration saved');
    } catch { toast.error('Failed to save configuration'); }
    finally { setConfigLoading(false); }
  };

  const isAdmin = ['CLIENT_ADMIN', 'SYSTEM_ADMIN'].includes(user.role);
  const isRecordKeeper = user.role === 'RECORD_KEEPER';
  const isScheduler = user.role === 'SCHEDULER';

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'drivers', label: 'Drivers', icon: Users },
    { id: 'vehicles', label: 'Vehicles', icon: Truck },
    ...(isAdmin ? [{ id: 'trailers', label: 'Trailers', icon: Container }] : []),
    ...((isAdmin || isRecordKeeper) ? [{ id: 'records', label: 'Record Keeper', icon: Edit3 }] : []),
    ...(isAdmin ? [
      { id: 'staff', label: 'Staff', icon: UserCog },
      { id: 'reports', label: 'Reports', icon: FileText },
      { id: 'data', label: 'Import/Export', icon: Upload },
      { id: 'provisional', label: 'Provisional', icon: AlertTriangle },
      { id: 'config', label: 'Business Rules', icon: Settings },
      { id: 'billing', label: 'Billing', icon: CreditCard },
      { id: 'archives', label: 'Archives', icon: Archive },
      { id: 'fleet-invites', label: 'Fleet Invites', icon: UserPlus },
    ] : []),
    ...(isScheduler ? [{ id: 'reports', label: 'Reports', icon: FileText }] : []),
    { id: 'compliance', label: 'Compliance', icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 ${sidebarOpen ? 'w-64' : 'w-0 lg:w-20'} bg-slate-800 border-r border-slate-700 transition-all duration-300 flex flex-col overflow-hidden`}>
        <div className="p-3 sm:p-4 flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <Truck className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          {sidebarOpen && <span className="font-bold text-white text-base sm:text-lg">LAWD</span>}
        </div>

        <nav className="flex-1 p-3 sm:p-4 space-y-1 sm:space-y-2">
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                if (window.innerWidth < 1024) setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl transition text-sm sm:text-base ${
                activeTab === item.id
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
              }`}
              data-testid={`menu-${item.id}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="p-3 sm:p-4 border-t border-slate-700">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-slate-400 hover:text-red-400 rounded-xl hover:bg-slate-700/50 transition text-sm sm:text-base"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto min-w-0">
        <header className="bg-slate-800/50 border-b border-slate-700 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700" data-testid="sidebar-toggle">
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-base sm:text-xl font-semibold text-white capitalize">{activeTab}</h1>
              <p className="text-xs sm:text-sm text-slate-400 hidden sm:block">{user.client_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <PushToggle />
            <NotificationDropdown
              notifications={notifications} unreadCount={unreadCount}
              showNotifications={showNotifications} setShowNotifications={setShowNotifications}
              onRefresh={loadNotifications}
            />
            <div className="text-right hidden sm:block">
              <p className="text-white font-medium text-sm sm:text-base">{user.first_name} {user.last_name}</p>
              <p className="text-xs sm:text-sm text-emerald-400">{user.role}</p>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-700 rounded-full flex items-center justify-center">
              <span className="text-white font-medium text-xs sm:text-base">{user.first_name?.[0]}{user.last_name?.[0]}</span>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6">
          {activeTab === 'overview' && <OverviewTab stats={stats} drivers={drivers} />}
          {activeTab === 'drivers' && <DriversPanel drivers={drivers} onRefresh={loadData} />}
          {activeTab === 'vehicles' && <VehiclesPanel vehicles={vehicles} onRefresh={loadData} />}
          {activeTab === 'trailers' && <TrailersPanel />}
          {activeTab === 'provisional' && (
            <ProvisionalTab
              provisionalVehicles={provisionalVehicles} pvLoading={pvLoading}
              onRefresh={loadProvisionalVehicles}
              onApprove={handleApproveVehicle} onReject={handleRejectVehicle}
            />
          )}
          {activeTab === 'config' && (
            <BusinessRulesTab
              clientConfig={clientConfig} setClientConfig={setClientConfig}
              statutoryRounding={statutoryRounding} setStatutoryRounding={setStatutoryRounding}
              configLoading={configLoading} onSave={handleSaveConfig}
              clientId={user?.client_id}
            />
          )}
          {activeTab === 'staff' && <StaffPanel user={user} />}
          {activeTab === 'reports' && <ReportsPanel drivers={drivers} />}
          {activeTab === 'data' && <DataImportExportPanel drivers={drivers} />}
          {activeTab === 'records' && <RecordKeeperPanel drivers={drivers} vehicles={vehicles} />}
          {activeTab === 'billing' && <SubscriptionPanel user={user} driverCount={drivers.length} />}
          {activeTab === 'archives' && <ArchiveReportsPanel drivers={drivers} />}
          {activeTab === 'fleet-invites' && <FleetInvitesPanel />}
          {activeTab === 'compliance' && <CompliancePanel drivers={drivers} />}
        </div>
      </main>
      <Toaster />
    </div>
  );
};

export default Dashboard;
